import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { LanguageGrid } from '../organisms/LanguageGrid';
import { LanguageBlade } from '../organisms/LanguageBlade';
import { useLanguageManagementStore } from '../../stores/language-management-store';
import { Language } from '../../types/translation-types';
import { toast } from 'sonner';

export function LanguageManagement() {
  const navigate = useNavigate();
  const {
    languages,
    selectedLanguage,
    isLoading,
    error,
    bladeOpen,
    deleteDialogOpen,
    languageToDelete,
    deleteUsageInfo,
    isDeleting,
    deleteError,
    fetchLanguages,
    deleteLanguage,
    reorderLanguages,
    openBlade,
    closeBlade,
    openDeleteDialog,
    closeDeleteDialog,
  } = useLanguageManagementStore();

  // Fetch languages on mount
  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const handleAddClick = useCallback(() => {
    openBlade();
  }, [openBlade]);

  const handleEditClick = useCallback(
    (language: Language) => {
      openBlade(language);
    },
    [openBlade]
  );

  const handleDeleteClick = useCallback(
    (language: Language) => {
      openDeleteDialog(language);
    },
    [openDeleteDialog]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!languageToDelete) return;

    const languageName = languageToDelete.languageName;
    const success = await deleteLanguage(languageToDelete.languageId);
    if (success) {
      toast.success(`Language "${languageName}" deleted successfully`);
    } else if (!useLanguageManagementStore.getState().deleteUsageInfo) {
      toast.error(useLanguageManagementStore.getState().deleteError || 'Failed to delete language');
    }
  }, [languageToDelete, deleteLanguage]);

  const handleRetry = useCallback(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const handleReorder = useCallback(
    async (reorderedLanguages: Language[]) => {
      try {
        await reorderLanguages(reorderedLanguages);
        toast.success('Languages reordered successfully');
      } catch {
        toast.error('Failed to reorder languages');
      }
    },
    [reorderLanguages]
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - fixed height */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/translations/languages')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Global Language Management</h1>
              <p className="text-muted-foreground">
                Add or remove languages available in the translation system
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddClick}>
              <Plus className="h-4 w-4 mr-2" />
              Add Language
            </Button>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <strong>Note:</strong> Languages added here become available globally across the translation system.
          After adding a new language, go back to{' '}
          <button
            onClick={() => navigate('/translations/languages')}
            className="underline hover:text-blue-900"
          >
            Language Settings
          </button>
          {' '}to assign it to specific applications.
        </div>
      </div>

      {/* Grid - flex-1 to fill remaining space */}
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-4">
        <LanguageGrid
          languages={languages}
          isLoading={isLoading}
          error={error}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onReorder={handleReorder}
          onRetry={handleRetry}
        />
      </div>

      {/* Add/Edit Blade */}
      <LanguageBlade isOpen={bladeOpen} onClose={closeBlade} language={selectedLanguage} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteUsageInfo ? 'Cannot Delete Language' : 'Delete Language'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {deleteUsageInfo ? (
                  <div className="space-y-3">
                    <p>
                      The language <strong>"{languageToDelete?.languageName}"</strong> cannot be
                      deleted because it is currently in use:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>
                        <strong>{deleteUsageInfo.translationCount}</strong> translations use this
                        language
                      </li>
                      {deleteUsageInfo.applicationNames.length > 0 && (
                        <li>
                          Used by applications:{' '}
                          <strong>{deleteUsageInfo.applicationNames.join(', ')}</strong>
                        </li>
                      )}
                    </ul>
                    <p className="text-sm text-gray-600 mt-2">
                      Remove the language from all applications and delete associated translations
                      before deleting this language.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p>
                      Are you sure you want to delete the language{' '}
                      <strong>"{languageToDelete?.languageName}"</strong> (
                      {languageToDelete?.languageCode})? This action cannot be undone.
                    </p>
                    {deleteError && (
                      <p className="text-sm text-red-600">{deleteError}</p>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteUsageInfo ? (
              <AlertDialogCancel>Close</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmDelete();
                  }}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
